import { LinkedContact } from "@/Common/Api/Patient/Dto/LinkedContact";
import { InlineCheckboxListControl } from "@/Common/Components/Inputs/InlineOptionsControl";
import { useTranslation } from "@/Common/CustomHooks/useTranslation";
import { ContactFacetType } from "@/Common/Models/Individuals/Enums/ContactFacetType";
import { Person } from "@/Common/Models/Person/Person";
import { RelationsHelper } from "@/Patient/Helpers/RelationsHelper";
import { relativeStore } from "@/Relative/Stores/RelativeStore";
import { RelationInfo } from "./RelationInfo";
import { RelativeIcon } from "./RelativeIcon";

import styles from "./RelationView.module.scss";

interface IRelationViewProps {
    linkedContact?: LinkedContact;
    selectedPerson?: Person;
    hidePermissions?: boolean;
    minWidth?: string;
    isReadOnly?: boolean;
}

function RelationView(props: IRelationViewProps): JSX.Element {
    const { getText } = useTranslation();

    const currentPerson = sessionStore.selectedFile;
    const person = props.linkedContact ? Person.FromLinkedContact(props.linkedContact) : props.selectedPerson;
    const representativeLabel: string = ` (${getText("RepresentativeLabel")}) - ${cacheStore.GetRAMQTypeRepresentant(true).filter(c => c[0] === props.linkedContact.RepresentativeType).map(b => b[1])}`;
    const emergencyContactLabel: string = ` - ${getText("EmergencyContactLabel")}`;
    const enableRelation = cacheStore.ClinicSettings.EnableRelations;
    const isClickable = person.ContactFacetType !== ContactFacetType.Relative || cacheStore.ClinicSettings.EnableRelations;

    const displayPermissionSection = cacheStore.ClinicSettings.EnablePortalInvitationFlowV2
        && (person.ContactFacetType === ContactFacetType.Patient
            || currentPerson.ContactFacetType === ContactFacetType.Patient);

    const openRelatedPersonFile = (): void => {
        if (props.isReadOnly)
            return;

        switch (person.ContactFacetType) {
            case ContactFacetType.Patient:
                patientStore.openPatient(person.ContactId);
                break;
            case ContactFacetType.Relative:
                if (!enableRelation)
                    break; // If flag is not enabled, do not open relative file;
                relativeStore.actions.loadFile(person.ContactId);
                break;
        }
    };

    return (
        <div
            className={`${styles.relationView} ${isClickable ? styles.selectable : null}`}
            onClick={() => openRelatedPersonFile()}
            style={{ minWidth: props.minWidth ?? "auto" }}
        >
            <RelativeIcon person={person} />
            <div className={styles.relationViewInfo}>
                <div className={styles.representative}>
                    <strong data-cy="relation-relationship">{props.linkedContact.RelationLookupId ? cacheStore.GetLocalizedValueByLookupId(props.linkedContact.RelationLookupId) : ""}</strong>
                    {props.linkedContact.IsRepresentative && <span>{representativeLabel}</span>}
                    {props.linkedContact.IsEmergencyContact && <span>{emergencyContactLabel}</span>}
                </div>
                <RelationInfo person={person} />
                {displayPermissionSection && !props.hidePermissions && (
                    <div className={styles.portalAccess}>
                        <h4 className={`thin-font ${styles.portalTitle}`}>{getText("PortalAccessRightsLabel")}</h4>
                        {person.ContactFacetType === ContactFacetType.Patient && (
                            <>
                                <span>
                                    <strong>{person.FirstName}</strong>
                                    {` ${getText("SharesWithLabel")} `}
                                    <strong>{currentPerson.FirstName}</strong>:
                                </span>
                                <div className={styles.portalOptions}>
                                    <InlineCheckboxListControl
                                        id="relationPortalAccess"
                                        items={RelationsHelper.getPermissionsOptions()}
                                        values={RelationsHelper.getPermissionsArray(props.linkedContact.ReversePermissionFlags)}
                                        disabled
                                    />
                                </div>
                            </>
                        )}
                        {currentPerson.ContactFacetType === ContactFacetType.Patient && (
                            <>
                                <span>
                                    <strong>{currentPerson.FirstName}</strong>
                                    {` ${getText("SharesWithLabel")} `}
                                    <strong>{props.linkedContact.Individual.FirstName}</strong>:
                                </span>
                                <InlineCheckboxListControl
                                    id="relatedRelationPortalAccess"
                                    items={RelationsHelper.getPermissionsOptions()}
                                    values={RelationsHelper.getPermissionsArray(props.linkedContact.PermissionFlags)}
                                    disabled
                                />
                            </>
                        )}
                    </div>
                )}
            </div>
        </div>
    );
}

export { RelationView };
